/**
 * Layout for attendance sheets: a roster down the side, a column for each school day, and
 * tables that paginate when the class is long. Positions are millimetres on a page from
 * `pageGeometry`; dates come from `calendar.ts`.
 */

import { daysInMonth, weekday } from "@/lib/tool-kit/printables/calendar";
import type { PageGeometry } from "@/lib/tool-kit/printables/page";
import { r2 } from "@/lib/tool-kit/printables/page";

export const MAX_ROSTER = 200;

/** Names, one per line. */
export function parseRoster(raw: string): string[] {
  return raw
    .split("\n")
    .map((line) => line.trim())
    .filter((line) => line.length > 0)
    .slice(0, MAX_ROSTER);
}

const DASHES = /[–—−]/g;
const SPLIT = /[\s,;]+/;
const RANGE = /^(\d{1,2})-(\d{1,2})$/;
const SINGLE = /^\d{1,2}$/;

/**
 * Days off as typed: "4, 11-13, 25" (commas, spaces or semicolons; en and em dashes for
 * ranges). Numbers outside the month are ignored; a reversed range counts both ways.
 */
export function parseDaysOff(raw: string, monthDays: number): Set<number> {
  const off = new Set<number>();
  for (const token of raw.replace(DASHES, "-").split(SPLIT)) {
    const range = RANGE.exec(token);
    if (range) {
      const a = Number(range[1]);
      const b = Number(range[2]);
      for (let day = Math.min(a, b); day <= Math.max(a, b); day += 1) {
        if (day >= 1 && day <= monthDays) {
          off.add(day);
        }
      }
    } else if (SINGLE.test(token)) {
      const day = Number(token);
      if (day >= 1 && day <= monthDays) {
        off.add(day);
      }
    }
  }
  return off;
}

export interface DayColumn {
  day: number;
  /** 0 = Sunday … 6 = Saturday. */
  weekday: number;
  off: boolean;
}

export interface DayOptions {
  saturdays: boolean;
  sundays: boolean;
  daysOff: ReadonlySet<number>;
  /** Drop days off from the table instead of shading them. */
  removeOff: boolean;
}

/** The month's school days as columns, in calendar order. */
export function dayColumns(
  year: number,
  month: number,
  options: DayOptions
): DayColumn[] {
  const columns: DayColumn[] = [];
  for (let day = 1; day <= daysInMonth(year, month); day += 1) {
    const dow = weekday(year, month, day);
    if ((dow === 6 && !options.saturdays) || (dow === 0 && !options.sundays)) {
      continue;
    }
    const off = options.daysOff.has(day);
    if (off && options.removeOff) {
      continue;
    }
    columns.push({ day, weekday: dow, off });
  }
  return columns;
}

export interface Col {
  x: number;
  width: number;
}

/** Columns of the given widths laid out left to right from `left`. */
export function splitColumns(left: number, widths: readonly number[]): Col[] {
  const columns: Col[] = [];
  let x = left;
  for (const width of widths) {
    columns.push({ x: r2(x), width: r2(width) });
    x += width;
  }
  return columns;
}

export interface MonthlyColumns {
  number: Col;
  name: Col;
  days: Col[];
  totals: Col[];
  right: number;
}

const NUMBER_WIDTH = 8;
const TOTAL_WIDTH = 14;

/** Columns of the monthly register: number, name, one per day, optional totals. */
export function monthlyColumns(
  geometry: PageGeometry,
  dayCount: number,
  totals: number
): MonthlyColumns {
  const nameWidth = Math.max(36, Math.min(60, geometry.contentWidth * 0.22));
  const dayArea =
    geometry.contentWidth - NUMBER_WIDTH - nameWidth - TOTAL_WIDTH * totals;
  const dayWidth = dayArea / Math.max(1, dayCount);
  const all = splitColumns(geometry.left, [
    NUMBER_WIDTH,
    nameWidth,
    ...Array.from({ length: dayCount }, () => dayWidth),
    ...Array.from({ length: totals }, () => TOTAL_WIDTH),
  ]);
  return {
    number: all[0] ?? { x: geometry.left, width: NUMBER_WIDTH },
    name: all[1] ?? { x: geometry.left, width: nameWidth },
    days: all.slice(2, 2 + dayCount),
    totals: all.slice(2 + dayCount),
    right: geometry.left + geometry.contentWidth,
  };
}

export interface WeeklyColumns {
  number: Col;
  name: Col;
  /** One entry per day, each with one or two sub-columns. */
  days: Col[][];
  right: number;
}

/** Columns of the weekly register: number, name, then each day (split AM/PM if asked). */
export function weeklyColumns(
  geometry: PageGeometry,
  dayCount: number,
  halves: boolean
): WeeklyColumns {
  const nameWidth = Math.max(36, Math.min(56, geometry.contentWidth * 0.28));
  const dayArea = geometry.contentWidth - NUMBER_WIDTH - nameWidth;
  const dayWidth = dayArea / Math.max(1, dayCount);
  const sub = halves ? 2 : 1;
  const base = splitColumns(geometry.left, [NUMBER_WIDTH, nameWidth]);
  const days: Col[][] = [];
  for (let d = 0; d < dayCount; d += 1) {
    days.push(
      splitColumns(
        geometry.left + NUMBER_WIDTH + nameWidth + d * dayWidth,
        Array.from({ length: sub }, () => dayWidth / sub)
      )
    );
  }
  return {
    number: base[0] ?? { x: geometry.left, width: NUMBER_WIDTH },
    name: base[1] ?? { x: geometry.left, width: nameWidth },
    days,
    right: geometry.left + geometry.contentWidth,
  };
}

/** Sign-in sheet columns: number, name, signature, time in, time out. */
export function signInColumns(geometry: PageGeometry): Col[] {
  const width = geometry.contentWidth;
  const time = 24;
  const number = NUMBER_WIDTH;
  const rest = width - number - time * 2;
  return splitColumns(geometry.left, [
    number,
    rest * 0.4,
    rest * 0.6,
    time,
    time,
  ]);
}

export interface TableRow {
  /** Position in the sheet, from 0. */
  index: number;
  y: number;
}

export interface TablePage {
  first: boolean;
  /** y of the table's header band. */
  headerY: number;
  rows: TableRow[];
}

export interface TableOptions {
  /** Space above the table on page one: title and write-in lines. */
  headerHeight: number;
  /** Height of the column-heading band. */
  tableHeader: number;
  minRow: number;
  maxRow: number;
  /** Space kept under the table for the key. */
  footer: number;
}

export interface TableLayout {
  pages: TablePage[];
  rowHeight: number;
}

/**
 * Rows flow down the page at a height between `minRow` and `maxRow` — taller when the
 * class is small — and onto more pages when the roster is long. Page two onward repeat
 * the column headings.
 */
export function layoutTable(
  count: number,
  geometry: PageGeometry,
  options: TableOptions
): TableLayout {
  const firstAvail =
    geometry.contentHeight -
    options.headerHeight -
    options.tableHeader -
    options.footer;
  const laterAvail =
    geometry.contentHeight - options.tableHeader - options.footer;
  const rowHeight = Math.max(
    options.minRow,
    Math.min(options.maxRow, firstAvail / Math.max(1, count))
  );
  const firstCap = Math.max(1, Math.floor(firstAvail / rowHeight + 0.0001));
  const laterCap = Math.max(1, Math.floor(laterAvail / rowHeight + 0.0001));
  const pages: TablePage[] = [];
  let next = 0;
  let first = true;
  while (next < count || first) {
    const capacity = first ? firstCap : laterCap;
    const headerY = geometry.top + (first ? options.headerHeight : 0);
    const rows: TableRow[] = [];
    for (let i = 0; i < capacity && next < count; i += 1) {
      rows.push({
        index: next,
        y: r2(headerY + options.tableHeader + i * rowHeight),
      });
      next += 1;
    }
    pages.push({ first, headerY: r2(headerY), rows });
    first = false;
    if (pages.length >= 20) {
      break;
    }
  }
  return { pages, rowHeight: r2(rowHeight) };
}
